import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  Checkbox,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description"; // Icon for documents
import useApiEp from "../hooks/useApiEp";

const MediaSelector = ({
  disabled = false,
  type = "image",
  selection = "single",
  selectionLimit = 1,
  onSelected,
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const {
    data: mediaItems,
    isLoading: loading,
    isError,
    error,
  } = useApiEp(`/upload/get-all-media?type=${type}`);
  // Helper function to infer media type from file extension
  const getMediaType = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "image";
      case "mp4":
      case "webm":
        return "video";
      case "pdf":
      case "doc":
      case "docx":
      case "txt":
        return "document";
      default:
        return "unknown";
    }
  };

  // Filter media items based on type and search term
  const filteredItems = useMemo(() => {
    if (!mediaItems) {
      return [];
    }
    return mediaItems.filter((item) => {
      const matchesSearch = item.file_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [mediaItems, searchTerm, type]);

  const handleSelect = useCallback(
    (item) => {
      let newSelectedItems;

      if (selection === "single") {
        newSelectedItems = [item];
      } else {
        const isSelected = selectedItems.some(
          (selected) => selected.mediaId === item.mediaId
        );

        if (isSelected) {
          newSelectedItems = selectedItems.filter(
            (selected) => selected.mediaId !== item.mediaId
          );
        } else {
          if (selectedItems.length >= selectionLimit) {
            return;
          }
          newSelectedItems = [...selectedItems, item];
        }
      }

      setSelectedItems(newSelectedItems);

      if (newSelectedItems.length === selectionLimit) {
        const mediaIds = newSelectedItems.map((item) => item.mediaId);
        onSelected?.(mediaIds.join(","));
      }
    },
    [selectedItems, selection, selectionLimit, onSelected]
  );

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    setSelectedItems([]);
  }, [selectionLimit]);

  if (loading) {
    return <LinearProgress />;
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading media: {error.message}
      </Alert>
    );
  }
  if (disabled) {
    return <></>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 1, // Slight rounding for aesthetics
        }}
      >
        <TextField
          placeholder={`Search ${type}s...`}
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            // High-contrast light gray
            startAdornment: <SearchIcon sx={{ mr: 1, color: "#ECEFF1" }} />,
          }}
          sx={{
            width: 300,
            backgroundColor: "#2E7D32", // Slightly lighter green for input field
            borderRadius: 1,
            "& .MuiInputBase-input": {
              color: "#F5F5F5", // Near-white text for maximum readability
              fontWeight: 400,
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#B0BEC5", // Light gray placeholder for contrast
              opacity: 1,
            },
          }}
          size="small"
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "#F5F5F5", fontWeight: 500 }}
          >
            Selection:{" "}
            {selection === "single"
              ? "Single"
              : `Multi (max ${selectionLimit})`}
          </Typography>
          {selectedItems.length > 0 && (
            <Chip
              label={`${selectedItems.length}/${selectionLimit} selected`}
              color="primary"
              size="small"
              sx={{
                backgroundColor: "#4CAF50", // Bright green for chip visibility
                color: "#F5F5F5", // Near-white text for contrast
                fontWeight: 500,
                border: "1px solid #81C784", // Light green border for emphasis
                "& .MuiChip-label": {
                  padding: "0 8px", // Adjust padding for better text visibility
                },
              }}
            />
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredItems.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", p: 4 }}
        >
          No {type}s found{searchTerm && ` matching "${searchTerm}"`}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredItems.map((item) => {
            const isSelected = selectedItems.some(
              (selected) => selected.mediaId === item.mediaId
            );
            const itemType = getMediaType(item.file_name);
            const isPdf = item.file_name.toLowerCase().endsWith(".pdf");

            return (
              <Grid item xs={6} sm={4} md={2} key={item.mediaId}>
                <Card
                  sx={{
                    position: "relative",
                    cursor: "pointer",
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? "primary.main" : "divider",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.02)",
                      borderColor: "primary.light",
                    },
                  }}
                  onClick={() => handleSelect(item)}
                >
                  <Box
                    sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                  >
                    <Checkbox
                      checked={isSelected}
                      sx={{
                        color: "white",
                        "&.Mui-checked": {
                          color: "white",
                        },
                        "& .MuiSvgIcon-root": {
                          filter: "drop-shadow(0px 2px 3px rgba(0,0,0,0.5))",
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      paddingTop: type === "image" ? "100%" : "56.25%",
                    }}
                  >
                    {type === "image" ? (
                      <CardMedia
                        component="img"
                        image={item.preview_url}
                        alt={item.file_name}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) =>
                          console.error(
                            `Failed to load image ${item.file_name}:`,
                            e
                          )
                        }
                      />
                    ) : type === "video" ? (
                      <CardMedia
                        component="video"
                        src={item.preview_url}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        controls
                        preload="metadata"
                        onError={(e) =>
                          console.error(
                            `Failed to load video ${item.file_name}:`,
                            e
                          )
                        }
                      />
                    ) : type === "document" ? (
                      isPdf ? (
                        <iframe
                          src={item.preview_url}
                          title={item.file_name}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: "none",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "grey.100",
                          }}
                        >
                          <DescriptionIcon
                            sx={{ fontSize: 40, color: "grey.600" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {item.file_name}
                          </Typography>
                        </Box>
                      )
                    ) : (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.100",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Unsupported media type
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? "bold" : "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.file_name}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default MediaSelector;
